import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Save, X, ArrowUp, ArrowDown, ImageIcon } from "lucide-react";
import { API, adminApi } from "./AdminDashboard";
import { PROGRAM_PAGES } from "@/data/programs";

const PROGRAMS = Object.entries(PROGRAM_PAGES).map(([slug, p]) => ({ slug, title: p.title }));
const inputCls = "w-full rounded-xl bg-white/70 border border-[#E2E8E4] px-4 py-2.5 text-sm text-[#1A2F24] placeholder:text-[#5C7368]/60 focus:outline-none focus:border-[#F4A261] transition-colors duration-300";

const ProgramGalleryTab = () => {
  const [slug, setSlug] = useState(PROGRAMS[0].slug);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = (s) => {
    setLoading(true);
    adminApi().get(`${API}/program-gallery/${s}`)
      .then((r) => setPhotos(r.data.photos || []))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(slug); }, [slug]);

  const upload = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await adminApi().post(`${API}/admin/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, { src: data.url, caption: "" }]);
      }
      toast.success("Photo(s) added — remember to Save.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const move = (i, dir) => setPhotos((p) => {
    const j = i + dir;
    if (j < 0 || j >= p.length) return p;
    const n = [...p];
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });
  const setCaption = (i, v) => setPhotos((p) => p.map((ph, idx) => (idx === i ? { ...ph, caption: v } : ph)));
  const remove = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await adminApi().put(`${API}/admin/program-gallery/${slug}`, { photos });
      toast.success("Gallery saved. It is live on the program page.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[#E2E8E4] bg-[#F2F4F0] p-7" data-testid="admin-program-gallery-tab">
      <h3 className="font-display text-2xl text-[#1A2F24]">Program Photos</h3>
      <p className="mt-2 text-sm text-[#5C7368]">Pick a program, then add, reorder, caption, or remove the photos shown in its gallery.</p>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <select value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="program-gallery-select" className={`${inputCls} max-w-xs`}>
          {PROGRAMS.map((p) => <option key={p.slug} value={p.slug}>{p.title}</option>)}
        </select>
        <label className={`inline-flex items-center gap-2 rounded-full border-2 border-dashed border-[#E2E8E4] bg-white/60 px-5 py-2.5 text-xs tracking-[0.12em] uppercase text-[#5C7368] cursor-pointer transition-colors duration-300 hover:border-[#F4A261] hover:text-[#F4A261] ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="program-gallery-upload">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {uploading ? "Uploading…" : "Add Photos"}
          <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => upload([...e.target.files])} />
        </label>
        <button onClick={save} disabled={saving} data-testid="program-gallery-save"
          className="inline-flex items-center gap-2 rounded-full bg-[#1E352A] text-[#FAF9F6] px-6 py-2.5 text-xs tracking-[0.12em] uppercase transition-colors duration-300 hover:bg-[#F4A261] hover:text-white disabled:opacity-60">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save Gallery
        </button>
      </div>

      {loading ? (
        <p className="mt-8 text-sm text-[#5C7368] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>
      ) : photos.length === 0 ? (
        <p className="mt-8 text-sm text-[#5C7368] flex items-center gap-2"><ImageIcon size={16} /> No photos yet. Use "Add Photos" to build this program's gallery.</p>
      ) : (
        <div className="mt-6 space-y-3" data-testid="program-gallery-list">
          {photos.map((ph, i) => (
            <div key={i} className="flex items-start gap-3 rounded-2xl border border-[#E2E8E4] bg-white/60 p-3" data-testid={`program-gallery-item-${i}`}>
              <img src={ph.src} alt={`Photo ${i + 1}`} className="w-24 h-24 rounded-xl object-cover border border-[#E2E8E4] shrink-0" />
              <div className="flex-1">
                <textarea rows={2} placeholder="Caption (optional)" value={ph.caption} onChange={(e) => setCaption(i, e.target.value)} data-testid={`program-gallery-caption-${i}`} className={inputCls} />
                <p className="mt-1 text-[10px] tracking-[0.15em] uppercase text-[#5C7368]">Photo {i + 1} of {photos.length}</p>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`program-gallery-up-${i}`} aria-label="Move up"
                  className="w-8 h-8 rounded-full border border-[#E2E8E4] flex items-center justify-center text-[#5C7368] hover:border-[#F4A261] hover:text-[#F4A261] disabled:opacity-30 transition-colors duration-300"><ArrowUp size={13} /></button>
                <button onClick={() => move(i, 1)} disabled={i === photos.length - 1} data-testid={`program-gallery-down-${i}`} aria-label="Move down"
                  className="w-8 h-8 rounded-full border border-[#E2E8E4] flex items-center justify-center text-[#5C7368] hover:border-[#F4A261] hover:text-[#F4A261] disabled:opacity-30 transition-colors duration-300"><ArrowDown size={13} /></button>
                <button onClick={() => remove(i)} data-testid={`program-gallery-remove-${i}`} aria-label="Remove photo"
                  className="w-8 h-8 rounded-full border border-[#E2E8E4] flex items-center justify-center text-[#5C7368] hover:border-red-400 hover:text-red-600 transition-colors duration-300"><X size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProgramGalleryTab;
